/* eslint-disable react/prop-types */
/* eslint-disable max-len */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Input,
  Modal,
  message,
  Tabs,
  Card,
  DatePicker,
} from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, RedoOutlined } from '@ant-design/icons';
import { formatReceived } from 'utils/formatValues';
import { onError, onSuccess } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import ModalConfirmation from 'components/ModalConfirmation';
import Select from 'components/Select';
import { getEstadosGlobales } from 'api/catalogos/index';
import { checkToken } from 'api/utils';
import API from 'utils/api';
import { NOMBRES_PATTERN } from 'utils/patterns';
import {
  ESTATUS_ACTIVO,
  ESTATUS_AUTORIZADO,
  ESTATUS_CANCELADO,
} from 'utils/constants';
import {
  autorizarConfiguracionExternaDescuento,
  deleteConfiguracionExternaDescuento,
  getConfiguracionExternaDescuento,
  getConfiguracionExternaDescuentos,
  patchConfiguracionExternaDescuento,
  postConfiguracionExternaDescuento,
} from 'api/catalogos/ConfiguracionCargaExternaDescuento';
import { getConceptosCargaExterna } from 'api/catalogos/ConceptosCargaExterna';
import { getModelPermission } from 'utils/permission';
import ConfiguracionCargaExternaDescuentoDetalle from './ConfiguracionCargaExternaDescuentoDetalle';

export const ConfiguracionCargaExternaDescuentoPermission = {
  permissionModel: 'descuentocargaexterna',
  catalogsModels: [],
  tabsModels: [],
};
const { TabPane } = Tabs;

const ConfiguracionCargaExternaDescuento = ({ permission }) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [estados, setEstados] = useState([]);
  const [visibleAutorizar, setVisibleAutorizar] = useState(false);
  const [selectedData, setSelectedData] = useState({});
  const [confirmationVisible, setConfirmationVisible] = useState(false);
  const [confirmationCancelarVisible, setConfirmationCancelarVisible] = useState(false);
  const [confirmationReactivarVisible, setConfirmationReactivarVisible] = useState(false);
  const [disableFields, setDisableFields] = useState(false);
  const [currentTabKey, setCurrentTabKey] = useState('general');
  const [conceptosCargaExterna, setConceptosCargaExterna] = useState([]);
  const [pageActual, setPageActual] = useState(1);
  const [isEditing, setIsEditing] = useState(false);

  const formInstances = [
    form,
  ];

  const fetchData = async () => {
    setLoading(true);
    const _data = await getConfiguracionExternaDescuentos();
    setData(formatReceived(_data));
    setLoading(false);
  };

  const fetchConceptos = async () => {
    if (!conceptosCargaExterna.length) {
      const conceptos = await getConceptosCargaExterna();
      setConceptosCargaExterna(conceptos);

      // Aquí hacemos fetch de estados globales solo después de obtener los conceptos
      const _estados = await getEstadosGlobales();
      setEstados(_estados);
    }
  };
  const baseURI = '/nominas/descuentos-carga-externa';
  const fetchPaginated = async (page = 1, q = null) => {
    try {
      setLoading(true);

      const cleanPage = Number(String(page).replace(/\D/g, '')); // solo dígitos
      const response = await API.get(baseURI, { params: { page: cleanPage, ...q } });

      const { results } = response.data;

      setSelectedRowKeys([]);
      setPageActual(cleanPage);
      setData({ ...response.data, results });
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };
  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _data,
      ] = await Promise.all([
        getConfiguracionExternaDescuentos(),
      ]);
      const { results } = _data;
      if (mounted) {
        setData({ ..._data, results });
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setVisible(false);
    setVisibleAutorizar(false);
    setConfirmationCancelarVisible(false);
    setConfirmationReactivarVisible(false);
    setDisableFields(false);
    setSelectedRowKeys([]);
    setSelectedData({});
  };
  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    try {
      const [key] = selectedRowKeys;

      const match = responseData || data.results.find((item) => item.id === key);

      if (!match) {
        message.warn('No se encontró el registro seleccionado');
        return;
      }

      form.resetFields();
      await fetchConceptos(); // asegúrate de tener los catálogos

      const formatted = formatReceived(match);
      // Agregar el campo esperado por el formulario:
      // formatted.concepto_carga_externa = match.concepto_carga_externa_id;

      form.setFieldsValue(formatted);
      setDisableFields(match?.estados_globales !== ESTATUS_ACTIVO);
      setVisible(true);
      setIsEditing(true);
    } catch (err) {
      onError(err, setLoading);
    } finally {
      setLoading(false);
    }
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      const [key] = selectedRowKeys;
      // eslint-disable-next-line max-len
      const saved = await (key ? patchConfiguracionExternaDescuento(key, values) : postConfiguracionExternaDescuento(values));
      if (saved) {
        if (_continue) {
          await fetchData();
          setSelectedRowKeys([saved.id]);
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(
        err,
        setLoading,
        formInstances,
      );
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deleteConfiguracionExternaDescuento(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    setSelectedData({ ...record });
    form.setFieldsValue(record);
  };

  const handleKeyPress = (e) => {
    if (/\d/.test(e.key)) {
      e.preventDefault();
    }
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = async () => {
    form.resetFields();
    onCancel(); // limpia estados y cierra otros modales
    await fetchConceptos(); //  Carga catálogo solo si no está cargado
    setIsEditing(false);
    setVisible(true); // abre el modal
  };

  const onClickDelete = () => {
    const [key] = selectedRowKeys;
    if (key) {
      setVisibleAlert(true);
    } else {
      setVisibleAlert(false);
      message.warn('Este campo no se puede borrar');
    }
  };

  const onConfirmAutorizar = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, 'No se puede autorizar concepto ,ya autorizado');
        return;
      }
      const saved = await autorizarConfiguracionExternaDescuento(key);
      if (saved) {
        const _data = await getConfiguracionExternaDescuento();
        setData(formatReceived(_data));
        onCancel();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const onConfirmCancelar = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        return;
      }
      const saved = await patchConfiguracionExternaDescuento(key, { estados_globales: ESTATUS_CANCELADO });
      if (saved) {
        const _data = await getConfiguracionExternaDescuento();
        setData(formatReceived(_data));
        onCancel();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const onConfirmReactivar = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        return;
      }
      const saved = await patchConfiguracionExternaDescuento(key, { estados_globales: ESTATUS_ACTIVO });
      if (saved) {
        const _data = await getConfiguracionExternaDescuento();
        setData(formatReceived(_data));
        onCancel();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const columns = [
    {
      titleText: 'Concepto',
      dataIndex: 'concepto_carga_externa_nombre',
      key: 'concepto_carga_externa_nombre',
      width: 400,
    },
    {
      titleText: 'Fecha final',
      dataIndex: 'fecha_final',
      key: 'fecha_final',
      width: 400,
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estado_global_descripcion',
      key: 'estado_global_descripcion',
      width: 200,
    },
  ];

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
    nombre: [
      requiredRule,
      {
        pattern: NOMBRES_PATTERN,
        message: 'Ingrese un nombre válido',
      },
    ],
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        {!open ? (
          <Table
            data={data?.results || []}
            pagination={false}
            paginationCallback={fetchPaginated}
            count={data?.count || 0}
            cols={columns}
            rowSelection={rowSelection}
            handleOnRowClick={handleOnRowClick}
            controls={{
              onClickAdd,
              onClickEdit: () => onClickEdit(),
              onClickDelete,
            }}
            customActions={[
              {
                onClick: () => setConfirmationVisible(true),
                permission: { model: 'descuentocargaexterna', action: 'autorizar' },
                text: 'Autorizar',
                icon: <CheckCircleOutlined />,
                disabledBySelection: false,
                className: 'success',
              },
              {
                onClick: () => setConfirmationCancelarVisible(true),
                permission: { model: 'descuentocargaexterna', action: 'cancelar' },
                text: 'Cancelar',
                icon: <CloseCircleOutlined />,
                disabledBySelection: false,
                className: 'cancel',
                disabled: !selectedRowKeys?.length || selectedData?.estados_globales === ESTATUS_CANCELADO,
              },
              {
                onClick: () => setConfirmationReactivarVisible(true),
                permission: { model: 'descuentocargaexterna', action: 'reactivar' },
                text: 'Reactivar',
                icon: <RedoOutlined />,
                disabledBySelection: false,
                className: 'success',
                disabled: !selectedRowKeys?.length || selectedData?.estados_globales === ESTATUS_ACTIVO,
              },
            ]}
            disableDelete={selectedData?.estados_globales === ESTATUS_AUTORIZADO}
            permission={permission}
            allowExportPDF={false}
          />
        ) : (
          <Card
            className="form-container"
            title={`${selectedRowKeys.length ? 'EDITAR' : 'AGREGAR'} CONFIGURACIÓN DE CARGA EXTERNA DE DESCUENTOS`}
            extra={(
              <FormSubmitControls
                onFinish={onFinish}
                onCancel={onCancel}
                loading={loading}
                onFinishText="Guardar y Continuar"
              />
            )}
            bordered={false}
          >
            <Tabs
              onChange={setCurrentTabKey}
              activeKey={currentTabKey}
              type="card"
            >
              <TabPane
                tab="CONFIGURACIÓN DE CARGA EXTERNA DE DESCUENTOS"
                key="general"
                forceRender
              >
                <Form
                  layout="vertical"
                  form={form}
                  name="general"
                  onFinish={() => onFinish()}
                  scrollToFirstError
                >
                  <Row gutter={10}>
                    {/* Concepto */}
                    <Col xs={24} sm={8}>
                      <Form.Item
                        name="concepto_carga_externa"
                        label="Concepto"
                        rules={rules.required}
                        hasFeedback
                      >
                        <Select
                          dataSource={conceptosCargaExterna} // <--- usa el catálogo completo
                          labelProp="nombre" // <--- del catálogo: nombre
                          keyProp="id" // <--- del catálogo: id
                          allowClear
                          showSearch
                          placeholder="Seleccione un concepto"
                          disabled={disableFields || isEditing}
                        />
                      </Form.Item>
                    </Col>

                    {/* Fecha Final */}
                    <Col xs={24} sm={8}>
                      <Form.Item
                        name="fecha_final"
                        label="Fecha Final"
                        hasFeedback
                      >
                        <DatePicker
                          style={{ width: '100%' }}
                          format="YYYY-MM-DD"
                          placeholder="Seleccione una fecha"
                          disabled={disableFields}
                        />
                      </Form.Item>
                    </Col>

                    {/* Estatus (solo lectura) */}
                    <Col xs={24} sm={24} md={8}>
                      <Form.Item
                        name="estados_globales"
                        label="Estatus"
                        rules={rules.estatus}
                        hasFeedback
                        initialValue={1}
                      >
                        <Select
                          labelProp="descripcion"
                          keyProp="id"
                          dataSource={estados}
                          disabled
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                      <Form.Item
                        name="columna_de_descripcion_del_concepto_externo"
                        label="Columna Descripción del Concepto Externo"
                        rules={[
                          ...rules.required,
                          {
                            pattern: /^[A-Za-z]{1,3}$/,
                            message: 'Ingrese una letra de columna válida (Ej: A, B, AA)',
                          },
                        ]}
                        normalize={(v) => v?.toUpperCase()}
                      >
                        <Input
                          placeholder="Ej: A"
                          disabled={disableFields}
                          maxLength={3}
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} sm={12} md={6}>
                      <Form.Item
                        name="columna_del_importe"
                        label="Columna del Importe"
                        rules={[
                          ...rules.required,
                          {
                            pattern: /^[A-Za-z]{1,3}$/,
                            message: 'Ingrese una letra de columna válida (Ej: A, B, AA)',
                          },
                        ]}
                        normalize={(v) => v?.toUpperCase()}
                      >
                        <Input
                          placeholder="Ej: C"
                          disabled={disableFields}
                          maxLength={3}
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} sm={12} md={6}>
                      <Form.Item
                        name="columna_del_nombre_del_empleado"
                        label="Columna Nombre del Empleado"
                        rules={[
                          ...rules.required,
                          {
                            pattern: /^[A-Za-z]{1,3}$/,
                            message: 'Ingrese una letra de columna válida (Ej: A, B, AA)',
                          },
                        ]}
                        normalize={(v) => v?.toUpperCase()}
                      >
                        <Input
                          placeholder="Ej: B"
                          disabled={disableFields}
                          maxLength={3}
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} sm={12} md={6}>
                      <Form.Item
                        name="columna_del_numero_del_empleado"
                        label="Columna Número del Empleado"
                        rules={[
                          ...rules.required,
                          {
                            pattern: /^[A-Za-z]{1,3}$/,
                            message: 'Ingrese una letra de columna válida (Ej: A, B, AA)',
                          },
                        ]}
                        normalize={(v) => v?.toUpperCase()}
                      >
                        <Input
                          placeholder="Ej: D"
                          disabled={disableFields}
                          maxLength={3}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                </Form>
                <ConfiguracionCargaExternaDescuentoDetalle
                  // eslint-disable-next-line max-len
                  ConfiguracionCargaExternaDescuento={selectedData}
                  permission={getModelPermission('descuentocargaexterna')}
                  hideParentForm={() => setVisible(false)}
                  showParentForm={() => setVisible(true)}
                />
              </TabPane>
            </Tabs>
          </Card>
        )}
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={form.getFieldValue('concepto_carga_externa_nombre') || ''}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={onConfirmAutorizar}
          ConfirmAutorize
          onCancel={() => {
            setConfirmationVisible(false);
          }}
          open={confirmationVisible}
          contentTitle={`autorizar el concepto "${form.getFieldValue('concepto_carga_externa_nombre')}"?`}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={onConfirmCancelar}
          ConfirmCancel
          onCancel={() => {
            setConfirmationCancelarVisible(false);
          }}
          open={confirmationCancelarVisible}
          contentTitle={`cancelar el concepto "${form.getFieldValue('concepto_carga_externa_nombre')}"?`}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={onConfirmReactivar}
          onFinishText="Reactivar"
          onFinishIcon={<RedoOutlined />}
          onCancel={() => {
            setConfirmationReactivarVisible(false);
          }}
          open={confirmationReactivarVisible}
          contentTitle={`reactivar el concepto "${form.getFieldValue('concepto_carga_externa_nombre')}"?`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default ConfiguracionCargaExternaDescuento;
